/**
 * Ajustes de ordem do OrdersRouter — lote padrão, tick mínimo e resolução
 * FUT/FUL. Extraído de ordersRouter.jsx na modularização dos god-objects;
 * corpos preservados byte-a-byte, `this` → `rt` (a instância Router).
 * Contrato pinado por __tests__/routerOrderAdjustmentsContract.test.js
 * (escrito ANTES da extração).
 *
 * Regra I1 (não violar): resolução de símbolo NUNCA é reimplementada aqui —
 * `adjustFutSymbol` (cancel-path, não lança) delega em marketdata.resolveSymbol
 * e `gateAndResolveFuturesOrder` (entry/modify, lança em stale) delega em
 * marketdata.resolveSymbolForTrading, o chokepoint gated de 08:55 (#318/#351).
 */
import marketdata from "./marketdata.jsx";

// Arredondamento decimal exato usado pelo tick mínimo (movido junto do bloco —
// era exclusivo de adjustMinPriceTick dentro do ordersRouter).
Number.prototype.round = function (places) {
  return +(Math.round(this + "e+" + places) + "e-" + places);
};

// Cache de 1 símbolo do MPT (evita lookup no hot path de digitação de preço).
var cachelastadjMinPriceTickValue = 0.01;
var cachelastadjMinPriceTickSymbol = "";

// Translate a FUT/FUL order symbol to its real contract. Delegates to the
// SINGLE resolution authority `marketdata.resolveSymbolForTrading`, which
// trusts the backend `O` field and enforces the 08:55 BRT freshness gate.
// NEVER reimplement resolution or expiration logic here — that two-place
// duplication WAS the bug (#318/WINFUT→WINQ26). If `O` is wrong, fix the
// backend. `resolveSymbolForTrading` throws (blocking the order) when symbols
// aren't fresh or the alias can't be translated.
// Non-throwing resolution (parent + OTO/OCO legs) via the display resolver.
// Safe for CANCEL — exiting a position must never be blocked by stale symbols.
// Order ENTRY/MODIFY uses `gateAndResolveFuturesOrder` instead.
export const adjustFutSymbol = (rt, order) => {
  if (!order) return;
  if (order.symbol) {
    order.symbol = marketdata.resolveSymbol(order.symbol);
  }
  if (Array.isArray(order.oto)) {
    order.oto.forEach((leg) => {
      if (leg && leg.symbol) {
        leg.symbol = marketdata.resolveSymbol(leg.symbol);
      }
    });
  }
};

// Throwing freshness gate for order ENTRY/MODIFY (place/replace) ONLY. Resolves
// and gates the parent AND every OTO/OCO leg through the single authority
// `marketdata.resolveSymbolForTrading`, which blocks the order (throws) when
// symbols aren't this trading day's set. NEVER call from cancelOrder.
export const gateAndResolveFuturesOrder = (rt, order) => {
  if (!order) return;
  if (order.symbol) {
    order.symbol = marketdata.resolveSymbolForTrading(order.symbol);
  }
  if (Array.isArray(order.oto)) {
    order.oto.forEach((leg) => {
      if (leg && leg.symbol) {
        leg.symbol = marketdata.resolveSymbolForTrading(leg.symbol);
      }
    });
  }
};

/**
 * @deprecated Use `marketdata.resolveSymbol()` (display) or
 * `marketdata.resolveSymbolForTrading()` (order path) directly.
 * Alias mantido para call sites legados de DISPLAY — delega ao ponto único.
 *
 * ⚠️  NÃO reescreva o body com lógica própria. A resolução vive SÓ em
 *     `marketdata`, que confia no campo `O` do backend. Duplicar a lógica
 *     aqui foi exatamente o bug #318.
 */
export const translateFutSymbol = (rt, symbol) =>
  marketdata.resolveSymbol(symbol);

export const checkStandardLot = (rt, symbol, qtt) => {
  if (qtt != "") {
    if (qtt != 0) {
      var stdlot = marketdata.getStandardLot(symbol);
      var x = qtt % stdlot;
      if (x == 0) {
        return true;
      }
    }
  }
  return false;
};

export const adjustStandardLot = (rt, symbol, qtt) => {
  let stdlot = marketdata.getStandardLot(symbol);
  if (qtt !== "" && qtt != 0) {
    let remainder = qtt % stdlot;
    if (remainder === 0) {
      return qtt;
    } else {
      // Round to nearest standard lot, but ensure minimum is stdlot
      let rounded = Math.round(qtt / stdlot) * stdlot;
      return Math.max(rounded, stdlot);
    }
  }
  return stdlot;
};

export const adjustMinPriceTick = (rt, symbol, px) => {
  let mpt = 0.01;
  if (symbol == cachelastadjMinPriceTickSymbol) {
    mpt = cachelastadjMinPriceTickValue;
  } else {
    mpt = marketdata.getMinInc(symbol);
    cachelastadjMinPriceTickSymbol = symbol;
    cachelastadjMinPriceTickValue = mpt;
  }
  let result = px;
  if (mpt >= 1) {
    result = (px / mpt).round(0) * mpt;
  } else if (mpt >= 0.49 && mpt <= 0.51) {
    result = (px / mpt).round(0) * mpt;
  } else {
    result = (px / mpt).round(0) * mpt;
  }
  return result;
};

export const adjustOrderPrices = (rt, order) => {
  if (order.limit)
    order.limit = rt.adjustMinPriceTick(order.symbol, order.limit);
  if (order.stop) order.stop = rt.adjustMinPriceTick(order.symbol, order.stop);
  if (order.oto) {
    for (let index = 0; index < order.oto.length; index++) {
      const o = order.oto[index];
      // ✅ FIX: Use main order symbol for OTO legs if leg symbol is missing
      const legSymbol = o.symbol || order.symbol;
      if (o.limit) o.limit = rt.adjustMinPriceTick(legSymbol, o.limit);
      if (o.stop) o.stop = rt.adjustMinPriceTick(legSymbol, o.stop);
    }
  }
};
